/**
 * createEditCell - Factory function to create edit cells based on field type
 *
 * This provides a simple way to generate renderEditCell functions
 * for any column based on the data type.
 *
 * Usage:
 * ```tsx
 * const columns = [
 *   {
 *     field: 'status',
 *     renderEditCell: createEditCell({
 *       type: 'select',
 *       options: statusOptions,
 *     }),
 *   },
 *   {
 *     field: 'owner',
 *     renderEditCell: createEditCell({
 *       type: 'id_array',
 *       config: { referenceType: 'user', multiple: false, ...ID_CONFIGS.user },
 *     }, { users }),
 *   },
 * ]
 * ```
 */

import { GridRenderEditCellParams } from '@mui/x-data-grid-premium'
import IdArrayEditCell from './IdArrayEditCell'
import SelectEditCell from './SelectEditCell'
import StringEditCell from './StringEditCell'
import { FieldType, SelectOption } from './types'

/** Options data that can be passed to edit cells */
export interface EditCellOptions {
  users?: any[]
  contacts?: any[]
  organizations?: any[]
  deals?: any[]
  products?: any[]
}

/**
 * Creates a renderEditCell function for a column
 */
export function createEditCell(fieldType: FieldType, options?: EditCellOptions) {
  return (params: GridRenderEditCellParams) => {
    switch (fieldType.type) {
      case 'string':
        return <StringEditCell {...params} inputType={fieldType.inputType} />

      case 'select':
        return <SelectEditCell {...params} options={fieldType.options} multiple={fieldType.multiple} />

      case 'id_array': {
        // Get available items based on reference type
        const availableItems = getAvailableItems(fieldType.config.referenceType, options)
        return <IdArrayEditCell {...params} config={fieldType.config} availableItems={availableItems} />
      }

      case 'number':
        // For now, fall back to string edit (can be enhanced later)
        return <StringEditCell {...params} inputType="text" />

      case 'date':
        // For now, fall back to string edit (can be enhanced later)
        return <StringEditCell {...params} inputType="text" />

      default:
        return <StringEditCell {...params} />
    }
  }
}

/**
 * Get available items for a reference type from options
 */
function getAvailableItems(referenceType: string, options?: EditCellOptions): any[] {
  if (!options) return []

  switch (referenceType) {
    case 'user':
      return options.users || []
    case 'contact':
      return options.contacts || []
    case 'organization':
      return options.organizations || []
    case 'deal':
      return options.deals || []
    case 'product':
      return options.products || []
    default:
      return []
  }
}

/**
 * Shorthand creators for common field types
 */

export const stringEditCell = (inputType?: 'text' | 'url' | 'email' | 'tel') =>
  createEditCell({ type: 'string', inputType })

export const selectEditCell = (options: SelectOption[], multiple = false) =>
  createEditCell({ type: 'select', options, multiple })

export const userEditCell = (opts: EditCellOptions, multiple = false) =>
  createEditCell(
    {
      type: 'id_array',
      config: {
        referenceType: 'user',
        multiple,
        idField: 'user_uuid',
        labelField: 'name',
        secondaryLabelField: 'email',
      },
    },
    opts
  )

export const contactEditCell = (opts: EditCellOptions, multiple = false) =>
  createEditCell(
    {
      type: 'id_array',
      config: {
        referenceType: 'contact',
        multiple,
        idField: 'contacts_uuid',
        labelField: 'name',
        secondaryLabelField: 'email',
      },
    },
    opts
  )

export const orgEditCell = (opts: EditCellOptions, multiple = false) =>
  createEditCell(
    {
      type: 'id_array',
      config: {
        referenceType: 'organization',
        multiple,
        idField: 'org_uuid',
        labelField: 'name',
      },
    },
    opts
  )
